import { AuthError } from './errors.ts'
import type { AuthConfig, IdentityProvider, TokenResponse } from './types.ts'

/**
 * The three hosted UI endpoints. Pure URL building and one POST each — no state, so the
 * session store can stay the only stateful thing in the package.
 */

/** `/oauth2/authorize` with PKCE. `identity_provider` sends the user straight to Google or Apple:
 * the same flow, so federated login needs no second code path (docs/design/frontend-v1.md §4, Auth
 * row). */
export function buildAuthorizeUrl(
  config: AuthConfig,
  params: {
    readonly challenge: string
    readonly state: string
    readonly identityProvider?: IdentityProvider
    readonly loginHint?: string
  },
): string {
  const url = new URL('/oauth2/authorize', config.hostedUiOrigin)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('client_id', config.clientId)
  url.searchParams.set('redirect_uri', config.redirectUri)
  url.searchParams.set('scope', config.scopes.join(' '))
  url.searchParams.set('state', params.state)
  url.searchParams.set('code_challenge', params.challenge)
  url.searchParams.set('code_challenge_method', 'S256')
  if (params.identityProvider) {
    url.searchParams.set('identity_provider', params.identityProvider)
  }
  if (params.loginHint) {
    url.searchParams.set('login_hint', params.loginHint)
  }
  return url.toString()
}

/**
 * `/logout`. Clears the hosted UI's own cookie, without which the next login is silently
 * re-authenticated and "log out" looks broken.
 */
export function buildLogoutUrl(config: AuthConfig): string {
  const url = new URL('/logout', config.hostedUiOrigin)
  url.searchParams.set('client_id', config.clientId)
  url.searchParams.set('logout_uri', config.postLogoutRedirectUri)
  return url.toString()
}

/** The `error` of an OAuth error body, or undefined when the body is not one. */
function oauthErrorOf(body: string): string | undefined {
  try {
    const parsed = JSON.parse(body) as { error?: unknown }
    return typeof parsed.error === 'string' ? parsed.error : undefined
  } catch {
    return undefined
  }
}

async function postToken(
  config: AuthConfig,
  body: URLSearchParams,
  failureCode: 'token_request_failed' | 'refresh_failed',
): Promise<TokenResponse> {
  const url = new URL('/oauth2/token', config.hostedUiOrigin)

  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
  } catch (cause) {
    // A network failure, not a rejection by Cognito: same code, but the cause is kept
    // so a log can tell an outage from bad credentials.
    throw new AuthError(failureCode, 'The token endpoint could not be reached', { cause })
  }

  if (!response.ok) {
    // The OAuth error body is small and English; it goes in the message for logs.
    const detail = await response.text().catch(() => '')
    throw new AuthError(
      failureCode,
      `The token endpoint answered ${response.status}${detail ? `: ${detail}` : ''}`,
      { terminal: response.status === 400 && oauthErrorOf(detail) === 'invalid_grant' },
    )
  }

  const payload: unknown = await response.json()
  if (!isTokenResponse(payload)) {
    throw new AuthError(failureCode, 'The token endpoint answered without an access_token')
  }
  return payload
}

/** Public client: `code_verifier` and no secret, which is the point of PKCE. */
export function exchangeCode(
  config: AuthConfig,
  params: { readonly code: string; readonly verifier: string },
): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code: params.code,
    redirect_uri: config.redirectUri,
    code_verifier: params.verifier,
  })
  return postToken(config, body, 'token_request_failed')
}

/**
 * With rotation enabled the response carries a NEW refresh token; the caller must store
 * it, because the one just used is now dead.
 */
export function refreshTokens(config: AuthConfig, refreshToken: string): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.clientId,
    refresh_token: refreshToken,
  })
  return postToken(config, body, 'refresh_failed')
}

function isTokenResponse(value: unknown): value is TokenResponse {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<TokenResponse>
  return typeof candidate.access_token === 'string' && typeof candidate.expires_in === 'number'
}
